import { type Flags, useFlag, useRequestFeedback } from '@reflag/react-sdk';
import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    type ButtonKind,
    type ButtonSize,
    type IconDefinition,
} from '@gitbook/spine-react';

interface FeedbackButtonHookProps {
    flagKey: keyof Flags;
    feedbackTitle?: string;
    openWithCommentVisible?: boolean;
}

/**
 * Hook to use the feedback button.
 */
export function useFeedbackButton(props: FeedbackButtonHookProps) {
    const {
        flagKey,
        feedbackTitle = 'How do you like GitBook?',
        openWithCommentVisible = false,
    } = props;
    const feature = useFlag(flagKey);
    const requestFeedback = useRequestFeedback();

    const [isOpened, setIsOpened] = React.useState(false);

    const onPress = React.useCallback(
        (event) => {
            requestFeedback({
                flagKey,
                title: feedbackTitle,
                position: {
                    type: 'POPOVER',
                    anchor: event.target as HTMLElement,
                },
                openWithCommentVisible,
                onClose: () => {
                    setIsOpened(false);
                },
            });
            setIsOpened(true);
        },
        [requestFeedback, flagKey, feedbackTitle, openWithCommentVisible]
    );
    return {
        isEnabled: feature.isEnabled,
        buttonProps: { onPress, 'aria-expanded': isOpened },
    };
}

/**
 * A button to share feedback.
 */
export function FeedbackButton(props: {
    kind?: ButtonKind;
    size?: ButtonSize;
    label?: string;
    icon?: IconDefinition;
    flagKey: keyof Flags;
    iconOnly?: boolean;
    feedbackTitle?: string;
    openWithCommentVisible?: boolean;
}) {
    const {
        kind = 'secondary',
        label = 'Share feedback',
        feedbackTitle = 'How do you like GitBook?',
        icon = Icon.Happy,
        iconOnly = false,
        size = 'small',
        flagKey,
        openWithCommentVisible,
    } = props;
    const { isEnabled, buttonProps } = useFeedbackButton({
        flagKey,
        feedbackTitle,
        openWithCommentVisible,
    });

    if (!isEnabled) {
        return null;
    }

    return (
        <Button
            {...buttonProps}
            iconOnly={iconOnly}
            kind={kind}
            size={size}
            label={label}
            leadingIcon={icon}
        />
    );
}
